import { useCurrentProject } from "@modules/projects/utils/useCurrentProject";
import { CostItemStatus, useAddBudgetItemMutation, useAddCostItemMutation, useBudgetItemsQuery, useEditBudgetItemMutation, useEditCostItemMutation, useGetProjectBudgetQuery, type BudgetItemsQuery, type GetProjectBudgetQuery } from "gql/index";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

export const useBudgetTableActions = () => {
  const { projectId } = useCurrentProject();
  const queryClient = useQueryClient();

  const budgetItemsKey = useBudgetItemsQuery.getKey({ projectId });
  const projectBudgetKey = useGetProjectBudgetQuery.getKey({ id: projectId });

  const defaultCostItemStatus = CostItemStatus.Planned;

  const calculateProjectBudget = (budgetItems: BudgetItemsQuery['budgetItems']) => {
    const totalBudgetedAmount = _.sumBy(budgetItems, item => Number(item.budgetedAmount ?? 0));
    const budgetItemsWithCalculatedAmounts = budgetItems.map(bi => ({
      budgetedAmount: bi.budgetedAmount,

      // effective amount of a cost item is the sum of expenses if the cost item is completed, otherwise the max of the sum of active quotes and expenses
      effectiveAmount: _.sumBy(bi.costItems, costItem => (
        (costItem.status === CostItemStatus.Completed 
          ? _.sumBy(costItem.entries, entry => Number(entry.expense?.expendedAmount ?? 0)) 
        : Math.max(Number(costItem.plannedAmount ?? 0), 
            Math.max(
              _.sumBy(costItem.entries, entry => Number(entry.quote?.amount ?? 0) 
              + (entry.expense?.quoteId == null ? Number(entry.expense?.expendedAmount ?? 0) : 0)), 
              _.sumBy(costItem.entries, entry => entry.expense?.expendedAmount ?? 0))))))
    }));

    const budgetOverrun = _.sumBy(budgetItemsWithCalculatedAmounts, item => Math.max(item.effectiveAmount - item.budgetedAmount, 0));

    return {
      totalBudgetedAmount,
      totalEffectiveAmount: _.sumBy(budgetItemsWithCalculatedAmounts, item => Number(item.effectiveAmount ?? 0)),
      budgetOverrun,
    };
  };

  const updateProjectBudgetQueryData = (budgetItems: BudgetItemsQuery['budgetItems']) => {
    const budget = calculateProjectBudget(budgetItems);

    queryClient.setQueryData<GetProjectBudgetQuery>(projectBudgetKey, (previous) => {
      if (!previous?.projectBudget) {
        return { projectBudget: budget };
      }

      return {
        projectBudget: {
          ...previous.projectBudget,
          ...budget
        }
      };
    });
  };

  const updateBudgetItemsQueryData = (updater: (items: BudgetItemsQuery['budgetItems']) => BudgetItemsQuery['budgetItems']) => {
    let updatedItems: BudgetItemsQuery['budgetItems'] | undefined;

    queryClient.setQueryData<BudgetItemsQuery>(budgetItemsKey, (previous) => {
      const currentItems = previous?.budgetItems ?? [];
      updatedItems = updater(currentItems);

      return {
        budgetItems: updatedItems,
      };
    });

    if (updatedItems) {
      updateProjectBudgetQueryData(updatedItems);
    }
  };

  const { mutate: addCostItem, isPending: isAddingCostItem } = useAddCostItemMutation({
    onSuccess: (data, variables) => {
      const newCostItemId = data?.addCostItem?.costItem?.id;
      const input = variables?.input;

      if (!newCostItemId || !input?.budgetItemId) {
        return;
      }

      updateBudgetItemsQueryData((items) => items.map(item => {
        if (item.id !== input.budgetItemId) {
          return item;
        }

        return {
          ...item,
          costItems: [
            {
              id: newCostItemId,
              name: input.name,
              plannedAmount: input.plannedAmount,
              status: defaultCostItemStatus,
              budgetItem: { id: input.budgetItemId },
              expenses: [],
              order: _.minBy(items.find(i => i.id === input.budgetItemId)?.costItems, c => c.order)?.order ?? 0 - 1,
              effectiveAmount: 0,
              entries: []
            },
            ...item.costItems,
          ]
        };

        
      }));
    }
  });

  const { mutate: addBudgetItem, isPending: isAddingRow } = useAddBudgetItemMutation({
    onSuccess: (data, variables) => {
      const newBudgetItemId = data?.addBudgetItem?.budgetItem?.id;
      const input = variables?.input;

      if (!newBudgetItemId || !input) {
        return;
      }

      updateBudgetItemsQueryData((items) => [
        {
          id: newBudgetItemId,
          name: input.name,
          budgetedAmount: input.budgetedAmount,
          order: _.minBy(items, item => item.order)?.order ?? 0 - 1,
          costItems: [],
        },
        ...items,
      ]);
    }
  });

  const { mutate: editBudgetItem, isPending: isUpdatingRow } = useEditBudgetItemMutation({
    onSuccess: (_, variables) => {
      const input = variables?.input;

      if (!input) {
        return;
      }

      updateBudgetItemsQueryData((items) => items.map(item => item.id === input.id ? {
        ...item,
        name: input.name,
        budgetedAmount: input.budgetedAmount
      } : item));
    }
  });

  const { mutate: editCostItem, isPending: isUpdatingCostItem } = useEditCostItemMutation({
    onSuccess: (_data, variables) => {
      const input = variables?.input;

      if (!input) {
        return;
      }

      updateBudgetItemsQueryData((items) => items.map(item => {
        if (item.id !== input.budgetItemId) {
          return item;
        }

        return {
          ...item,
          costItems: item.costItems.map(costItem => costItem.id === input.id ? {
            ...costItem,
            name: input.name,
            plannedAmount: input.plannedAmount,
            status: input.status,
            effectiveAmount: input.status === CostItemStatus.Completed 
            ? _.sumBy(costItem.entries, entry => entry.expense?.expendedAmount ?? 0) 
            : Math.max(input.plannedAmount, Math.max(
              _.sumBy(costItem.entries, entry => Number(entry.quote?.amount ?? 0) 
              + (entry.expense?.quoteId == null ? Number(entry.expense?.expendedAmount ?? 0) : 0)), 
              _.sumBy(costItem.entries, entry => entry.expense?.expendedAmount ?? 0))),
            budgetItem: { id: input.budgetItemId },
          } : costItem)
        };
      }));
    }
  });


  return {
    addCostItem,
    addBudgetItem,
    editBudgetItem,
    editCostItem,
    isPending: isAddingCostItem || isAddingRow || isUpdatingRow || isUpdatingCostItem,
  };
};